import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import { integrationsPageSchema } from '../components/StructuredData';
import './Features.css';

const Integrations: React.FC = () => {
  return (
    <div className="feature-page">
      <SEO
        title="Integrations - TableChat | Klaviyo, Mailchimp, Constant Contact & More"
        description="TableChat integrates with Klaviyo, Mailchimp, Constant Contact, and other marketing platforms to sync guest contact data captured at the table. Build segmented email lists, trigger automated campaigns, and drive repeat visits — all from data collected during the meal."
        canonical="https://tablechat.io/features/integrations"
      />
      <StructuredData data={integrationsPageSchema} />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Features', url: 'https://tablechat.io/features/integrations' },
        { name: 'Integrations', url: 'https://tablechat.io/features/integrations' },
      ])} />

      {/* Hero */}
      <section className="feature-hero feature-hero-split">
        <div className="container">
          <div className="feature-hero-grid">
            <div className="feature-hero-text">
              <h1>Connect TableChat to the Tools You Already Use</h1>
              <p className="hero-lead">
                Guest data captured at the table flows directly into your marketing stack. Sync contact 
                information with Klaviyo, Mailchimp, Constant Contact, and other platforms your business 
                already uses — turning first-time visitors into repeat customers without manual data entry 
                or middleware.
              </p>
            </div>
            <div className="feature-hero-image">
              <img 
                src="/integrations-hero.png"
                alt="Guest joining VIP list on phone with Klaviyo, Mailchimp, and Constant Contact integration arrows" 
                className="feature-hero-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Integration Cards */}
      <section className="feature-problem-solution">
        <div className="container">
          <div className="section-intro">
            <h2>Marketing Integrations Built for Restaurants</h2>
            <p>
              Most restaurant guest data lives in a spreadsheet — or nowhere at all. TableChat captures 
              it at the table and pushes it to your platform of choice automatically.
            </p>
          </div>

          <div className="integration-cards">
            <div className="integration-card">
              <div className="integration-icon">📧</div>
              <h3>Klaviyo</h3>
              <p>
                Sync guest profiles to Klaviyo for segmented email and SMS campaigns. Build flows triggered 
                by VIP enrollment date or contact segment. Target guests with personalized 
                offers based on their location and enrollment context.
              </p>
              <span className="integration-badge">Live Integration</span>
            </div>

            <div className="integration-card">
              <div className="integration-icon">✉️</div>
              <h3>Mailchimp</h3>
              <p>
                Push guest contact data to Mailchimp audiences automatically. Build email campaigns, 
                automate welcome sequences for new VIP members, and segment by restaurant location. 
                Works with Mailchimp's standard and premium plans.
              </p>
              <span className="integration-badge">Live Integration</span>
            </div>

            <div className="integration-card">
              <div className="integration-icon">📬</div>
              <h3>Constant Contact</h3>
              <p>
                Sync guest contact data to Constant Contact for email campaigns, event promotions, 
                and automated welcome sequences. A trusted platform used by thousands of restaurants 
                for simple, effective direct marketing.
              </p>
              <span className="integration-badge">Live Integration</span>
            </div>

            <div className="integration-card">
              <div className="integration-icon">🔗</div>
              <h3>And More</h3>
              <p>
                Need to connect to a different platform? TableChat supports CSV export and can integrate 
                with additional marketing tools as your business requires. We add new integrations based 
                on client needs.
              </p>
              <span className="integration-badge">Custom Integration</span>
            </div>
          </div>
        </div>
      </section>

      {/* How Data Flows */}
      <section className="feature-how-it-works">
        <div className="container">
          <h2>How the Data Flows</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h4>Guest Opts In</h4>
              <p>Guest taps the NFC tag, enters the chat, and enrolls in your VIP program with their email or phone number.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <h4>Data Syncs</h4>
              <p>Contact information is captured in your TableChat management console and synced to your connected marketing platform.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <h4>Campaigns Activate</h4>
              <p>Use your marketing platform to send welcome emails, promotional offers, and targeted campaigns — automatically.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem-Solution Detail */}
      <section className="feature-problem-solution">
        <div className="container">
          <div className="feature-blocks">
            <div className="feature-block">
              <div className="feature-block-content">
                <h3>Why This Matters for Restaurant Owners</h3>
                <p>
                  Restaurants spend heavily on acquiring new customers through third-party delivery apps, 
                  social media ads, and review platforms — all of which own the customer relationship, not you. 
                  When a guest walks out your door, you have no way to reach them again.
                </p>
                <p>
                  TableChat changes that equation. Every meal becomes an opportunity to capture a direct 
                  relationship with the guest. And because the data syncs to industry-standard marketing 
                  platforms, you can run the same sophisticated campaigns that e-commerce brands use — 
                  welcome series, seasonal promotions, re-engagement campaigns — all built on first-party 
                  data you own.
                </p>
              </div>
              <div className="feature-visual">
                <h4>What You Can Build</h4>
                <ul>
                  <li>Welcome email series for new VIP members</li>
                  <li>Re-engagement campaigns based on enrollment date</li>
                  <li>Seasonal and holiday promotional offers</li>
                  <li>Event and new menu launch announcements</li>
                  <li>Location-specific campaigns for multi-unit operators</li>
                  <li>SMS campaigns for time-sensitive promotions</li>
                </ul>
              </div>
            </div>

            <div className="feature-block reverse">
              <div className="feature-block-content">
                <h3>No Developer Required</h3>
                <p>
                  Integrations are configured from your TableChat management console. Select your platform, 
                  authenticate with your account, and choose which data fields to sync. The setup takes 
                  minutes, not days, and doesn't require an API developer or IT support.
                </p>
                <p>
                  Once connected, data flows automatically for every new guest enrollment. You can monitor 
                  sync status from the console and export data manually if needed for backup or 
                  additional platforms.
                </p>
              </div>
              <div className="feature-visual">
                <h4>Setup Is Simple</h4>
                <ul>
                  <li>Configure from the management console — no code</li>
                  <li>Authenticate with your marketing platform account</li>
                  <li>Choose data fields to sync (email, phone)</li>
                  <li>Automatic sync for every new guest enrollment</li>
                  <li>Manual export available as CSV backup</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="feature-stats">
        <div className="container">
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-number">3+</div>
              <p className="stat-label">Marketing platforms supported — Klaviyo, Mailchimp, Constant Contact, and more</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">Auto</div>
              <p className="stat-label">Guest data syncs automatically on enrollment</p>
            </div>
            <div className="stat-card">
              <div className="stat-number">0</div>
              <p className="stat-label">Developer hours required to set up integrations</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="feature-cta">
        <div className="container">
          <h2>Own Your Guest Relationships</h2>
          <p>Stop renting customers from third-party platforms. Capture guest data at the table and market to them directly.</p>
          <Link to="/signup" className="btn btn-primary btn-large">Get Started Today</Link>
        </div>
      </section>
    </div>
  );
};

export default Integrations;
